// Helpers PUROS del Panel del cliente V11 (UX 2.1). Sin React y sin i18n adentro: los textos de
// producto salen por el `t` que recibe cada helper, así el módulo se testea sin montar nada.
//
// Vive acá y no en `src/utils/` porque son las decisiones de presentación DE ESTE panel (cuándo un
// número se compacta, qué color le corresponde a una proyección). Lo que ya existía se reusa:
// el semáforo del gasto es literalmente el del Listado de Clientes v2 (`clientsV2Thresholds`).

import {
    SEVERITY_GOLD,
    SEVERITY_GREEN,
    SEVERITY_RED,
    spendState,
} from '../../agency/v2/clientsV2Thresholds';

// Los importes del panel son plata de una agencia argentina y se leen de un vistazo: separador de
// miles con punto y decimal con coma. `es-AR` fijo (no el locale del navegador) para que el número
// no cambie de forma según quién mira — el idioma de la UI lo maneja i18n, no el formato del monto.
export const LOCALE = 'es-AR';
const MILLION = 1000000;

/**
 * Monto compacto para KPIs y barras: `ARS 33,4M`, `ARS 180.000`.
 *
 * Dos tramos a propósito (mockup V11):
 * - Debajo del millón, el número ENTERO con separadores ("180.000"). Un "180K" no ahorra espacio y
 *   obliga a hacer la cuenta mental; el panel muestra plata, no telemetría.
 * - Del millón para arriba, millones con un decimal ("33,4M", "55M" — el ",0" se cae solo). A
 *   partir de los mil millones el decimal es ruido: "2.500M".
 *
 * `null`/`undefined`/no-numérico → `null` (el llamador decide si pinta "—" o esconde la fila).
 * `currency` vacío → sólo el número.
 */
export function formatCompactMoney(currency, amount) {
    const compact = formatCompactAmount(amount);
    if (compact === null) return null;
    return currency ? `${currency} ${compact}` : compact;
}

/**
 * Monto COMPLETO para las celdas de trabajo: `ARS 1.230.000`. En la vista de todas las líneas el
 * presupuesto se compara y se edita fila contra fila, y ahí el compacto pierde información (un
 * "1,2M" no distingue 1.230.000 de 1.190.000). `null`/no numérico → `null`.
 */
export function formatFullMoney(currency, amount) {
    if (amount === null || amount === undefined || amount === '') return null;
    const value = Number(amount);
    if (!Number.isFinite(value)) return null;
    const text = (Math.round(value) || 0).toLocaleString(LOCALE);
    return currency ? `${currency} ${text}` : text;
}

/** El número compacto sin moneda (mismas reglas que `formatCompactMoney`). */
export function formatCompactAmount(amount) {
    if (amount === null || amount === undefined || amount === '') return null;
    const value = Number(amount);
    if (!Number.isFinite(value)) return null;

    if (Math.abs(value) < MILLION) {
        // `|| 0` mata el -0 que devuelve Math.round para valores como -0,4 (se muestra "-0").
        return (Math.round(value) || 0).toLocaleString(LOCALE);
    }

    const millions = value / MILLION;
    const digits = Math.abs(millions) >= 1000 ? 0 : 1;
    return `${millions.toLocaleString(LOCALE, {maximumFractionDigits: digits})}M`;
}

/**
 * El valor de un objetivo (`lines[].goal.current_value` / `target_value`), que NO siempre es plata:
 * puede ser ingresos ("5.000.000"), conversiones ("115") o un ratio ("3,5" de ROAS).
 *
 * Por eso no alcanza con `formatCompactAmount`, que redondea a entero debajo del millón y
 * convertiría un objetivo de ROAS 3,5 en "4" — un número que el usuario nunca cargó. Debajo de mil
 * se conservan hasta dos decimales (y los ceros a la derecha se caen solos: "115", no "115,00"); de
 * ahí para arriba manda el formato compacto del panel, que es lo que hace que el tile se lea de un
 * vistazo. `null`/no numérico → `null`.
 */
export function formatGoalValue(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    if (Math.abs(number) < 1000) return number.toLocaleString(LOCALE, {maximumFractionDigits: 2});
    return formatCompactAmount(number);
}

// --- Semáforos --------------------------------------------------------------------------------

// Proyección de cierre (`kpis.projection.status` y `lines[].projection.status`). Mapeo FIJO del
// mockup: quedarse corto es lo caro (presupuesto que no se gastó = plata que el cliente no invirtió
// y trabajo que no se facturó), así que UNDER es el rojo; pasarse avisa en oro.
export const PROJECTION_SEVERITY_BY_STATUS = {
    ON_TRACK: SEVERITY_GREEN,
    OVER: SEVERITY_GOLD,
    UNDER: SEVERITY_RED,
};

/**
 * `ON_TRACK`→verde, `OVER`→oro, `UNDER`→rojo. `null`/desconocido → `null`: sin presupuesto o sin
 * nada computable NO hay veredicto, y el panel no debe inventar un color (ni verde ni gris) para
 * algo sobre lo que el backend no opinó.
 */
export function projectionSeverity(status) {
    if (!status) return null;
    const severity = PROJECTION_SEVERITY_BY_STATUS[status];
    return severity === undefined ? null : severity;
}

/**
 * Color del nivel de alerta de presupuesto de una línea (`lines[].budget_alert_level`):
 * `CRITIC`→rojo, `BAD`→oro, null→gris ("todavía no hay evaluación", no "está bien").
 *
 * DELEGA en `spendState` del Listado de Clientes v2 a propósito: es el MISMO veredicto (la
 * evaluación de alertas que configuró la agencia) y tiene que pintarse igual en las dos pantallas.
 * Cuando el listado dice rojo y el panel del mismo cliente dice verde, el usuario deja de creerle a
 * los dos. `spendState` sólo mira `budget_alert_level`, así que el objeto sintético alcanza.
 */
export function alertLevelSeverity(level) {
    return spendState({budget_alert_level: level});
}

// --- Etiquetas --------------------------------------------------------------------------------

/**
 * "día 18 de 31" — dónde está parado el período (`kpis.month` / `lines[].period`). Sin esto, un
 * 53% consumido no se puede juzgar.
 *
 * Firma `(day, days, t)`: el texto es de producto, así que sale por i18n (`client_panel.day_of_period`).
 * Datos inválidos → `null`.
 */
export function dayOfPeriodLabel(day, days, t) {
    const dayValue = Number(day);
    const daysValue = Number(days);
    // Los días del período son 1-based: un 0 acá es `null`/`''` colándose por Number(), no un día.
    if (!(dayValue >= 1) || !(daysValue >= 1)) return null;
    return t('client_panel.day_of_period', {day: dayValue, days: daysValue});
}

/**
 * "hoy" / "ayer" / "hace N días" para los timestamps del panel (último login del portal, última
 * consulta del MCP, última sincronización del media plan, fecha de un hecho).
 *
 * Se compara por DÍA CALENDARIO local, no por horas: algo de las 23:50 de ayer es "ayer" aunque
 * hayan pasado 20 minutos. Fechas futuras (relojes corridos) caen en "hoy" en vez de mostrar
 * "hace -1 días". Fecha vacía o inválida → `null`.
 */
export function relativeTimeLabel(isoDate, t, now = new Date()) {
    const days = daysSince(isoDate, now);
    if (days === null) return null;
    if (days <= 0) return t('client_panel.rel_today');
    if (days === 1) return t('client_panel.rel_yesterday');
    return t('client_panel.rel_days_ago', {n: days});
}

/** Días calendario locales transcurridos desde `isoDate` (0 = hoy). `null` si no se puede parsear. */
export function daysSince(isoDate, now = new Date()) {
    const date = parsePanelDate(isoDate);
    if (!date) return null;
    const diffMs = startOfDay(now) - startOfDay(date);
    return Math.round(diffMs / 86400000);
}

/**
 * Parser de las fechas del contrato. Un `YYYY-MM-DD` pelado (`occurred_on`, `last_meeting_on`,
 * `created_on`) se interpreta como día LOCAL: `new Date('2026-07-10')` es medianoche UTC y en
 * Argentina cae el 9 — el clásico off-by-one que haría decir "ayer" a algo de hoy. Los timestamps
 * completos (`...T10:00:00Z`) sí van por el parser nativo.
 */
export function parsePanelDate(value) {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string') {
        const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (dateOnly) {
            return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
        }
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
}

// --- Prompts ----------------------------------------------------------------------------------

// Meses en español NEUTRO y en minúscula (el placeholder cae en medio de una oración: "...en julio
// 2026, hacé una auditoría..."). Es la MISMA lista que el backend (`MONTH_NAMES_ES` en
// `agent_tools/services/moment_prompt_service.py`): el prompt del momento y el del rotador de
// fallback tienen que leerse igual, si no el usuario ve dos redacciones para la misma idea.
const MONTH_NAMES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

// El vocabulario COMPLETO de placeholders del catálogo, medido sobre la fuente
// (`agent_tools/fixtures/prompt_library_catalog.yaml`) el 2026-09-09 — no sobre los tokens que ya
// conocíamos, que es como se coló el agujero que arreglan estas líneas:
//
//   {cliente} ×54 · {período} ×37 · {alerta} ×9 · {línea} ×9 · {canal} ×1
//
// Se parten en tres grupos, y el grupo decide qué puede hacer cada superficie:
//
// 1. RESUELVE: `{cliente}` y `{período}`. Cualquier pantalla que hable de UN cliente y de HOY los
//    sabe completar.
// 2. LITERAL A PROPÓSITO: `{canal}`. El único prompt que lo usa le está pidiendo al usuario que
//    elija Meta o Google; completarlo sería decidir el análisis por él (misma decisión que el
//    backend).
// 3. NO RESOLUBLE ACÁ: `{alerta}` y `{línea}`. Los completa el HECHO que disparó el prompt, y sólo
//    existen en la familia `alerta-*`, que el producto propone cuando hay una alerta viva
//    (`MomentPromptService`). En una grilla que el usuario recorre a mano no hay ninguna alerta que
//    los llene: ver `hasUnresolvablePlaceholders`.
//
// ⚠️ Este vocabulario está escrito a mano en TRES lugares y ninguno importa al otro: acá, en
// `official_prompt_service.py` (2 tokens) y en `moment_prompt_service.py` (4 tokens). El de acá era
// un subconjunto estricto del último, y por eso `{alerta}`/`{línea}` llegaban crudos al
// portapapeles. Si el catálogo suma un placeholder, esta lista es una de las tres que hay que tocar.
const PLACEHOLDER_CLIENT = '{cliente}';
// Dos grafías, porque el backend acepta las dos (`official_prompt_service.LIBRARY_TOKENS_PERIOD`) y
// el hint del modal de creación sólo publicita la de la tilde. Un prompt que la agencia escriba
// con `{periodo}` es un placeholder legítimo allá; con una sola grafía acá, el FE era el más
// estricto de los tres y lo copiaba crudo — justo para la población que este frente promueve al
// tope de la grilla.
const PLACEHOLDERS_PERIOD = ['{período}', '{periodo}'];

// Grupo 3: lo que ESTA superficie no puede completar. Lista explícita y cerrada, nunca un regex de
// `\{.*\}`: los prompts traen llaves literales en sus ejemplos (`{rows: [{Costo: 1234}]}`) y un
// regex genérico los reportaría como placeholders pendientes. Mismo criterio que
// `unresolved_library_placeholders` en el backend.
export const UNRESOLVABLE_PLACEHOLDERS = ['{alerta}', '{línea}'];

/**
 * ¿Este texto conserva algún placeholder que la pantalla no puede completar?
 *
 * Lo usa la banda «Usa tu IA» para no ofrecer «Copiar» de un prompt que saldría incompleto. La
 * pregunta se hace sobre el TEXTO y no sobre el slug del prompt a propósito: un filtro por
 * `alerta-*` quedaría atado a una convención de nombres del catálogo y no cubriría un prompt que
 * escriba una agencia con esos tokens.
 */
export function hasUnresolvablePlaceholders(text) {
    if (!text) return false;
    return UNRESOLVABLE_PLACEHOLDERS.some(token => text.indexOf(token) !== -1);
}

/**
 * El texto de un prompt con los placeholders que el panel sabe completar ya reemplazados:
 * `{cliente}` → el nombre del cliente, `{período}`/`{periodo}` → el mes en curso ("julio 2026").
 *
 * Existe porque la biblioteca (`GET agent/prompt-library/`) devuelve el texto CRUDO del catálogo a
 * propósito —ahí el usuario elige una plantilla que puede aplicar a otro cliente o a otro período—,
 * pero cuando ese prompt entra al rail del Panel es de ESTE cliente y de HOY: mostrar «Para
 * «{cliente}» en {período}...» en pantalla (y peor, copiarlo así) es un bug. El prompt del momento
 * que manda el backend ya viene resuelto con esta misma semántica (`MomentPromptService._resolved_text`).
 *
 * ⚠️ **La comparte la banda «Usa tu IA»** (`BoosterUseIaBand`), que tenía el mismo bug en sus tres
 * superficies de copiado (card J7eyNTzE). Es la única función de este módulo que se usa fuera del
 * Panel, y a propósito: dos implementaciones del mismo reemplazo garantizan que en algún momento
 * una pantalla resuelva `{período}` y la otra no.
 *
 * Defensivo por construcción, igual que el backend: es un reemplazo por placeholder CONOCIDO, así
 * que un texto sin placeholders vuelve idéntico, uno desconocido (`{loquesea}`) viaja literal y un
 * valor que no se pudo resolver (cliente sin nombre) deja el placeholder a la vista — nunca se lo
 * cambia por vacío ni por "undefined", que es peor. Por lo mismo es idempotente: aplicarlo sobre un
 * texto ya resuelto no cambia nada.
 *
 * `date` es el "hoy" con el que se completa el período (se recibe para poder testearlo sin el reloj
 * real); acepta `Date` o el `YYYY-MM-DD` del contrato.
 */
export function resolvePromptPlaceholders(text, {clientName, date} = {}) {
    if (!text) return text;

    const values = {
        [PLACEHOLDER_CLIENT]: (clientName || '').trim() || null,
    };
    PLACEHOLDERS_PERIOD.forEach((token) => { values[token] = currentMonthLabel(date); });

    // `split`/`join` y no `replace`: un prompt puede nombrar al cliente más de una vez y `replace`
    // con string sólo cambia la PRIMERA aparición.
    return Object.keys(values).reduce((resolved, placeholder) => (
        values[placeholder] ? resolved.split(placeholder).join(values[placeholder]) : resolved
    ), text);
}

/** "julio 2026" — el mes de `date` (o el de hoy) en lenguaje natural y español neutro. */
export function currentMonthLabel(date) {
    const day = parsePanelDate(date) || new Date();
    return `${MONTH_NAMES_ES[day.getMonth()]} ${day.getFullYear()}`;
}

// --- Números ----------------------------------------------------------------------------------

/**
 * Porcentaje consumido de un presupuesto, redondeado. Sin presupuesto (null, 0 o no numérico) no
 * hay porcentaje posible → `null` (la línea muestra "Sin presupuesto", nunca 0% ni ∞).
 */
export function spendPct(spend, budget) {
    const budgetValue = Number(budget);
    if (!Number.isFinite(budgetValue) || budgetValue === 0) return null;
    const spendValue = Number(spend);
    if (!Number.isFinite(spendValue)) return null;
    return Math.round((spendValue / budgetValue) * 100);
}

/**
 * Un porcentaje clavado entre 0 y 100 para dibujar barras: un 128% consumido no puede desbordar la
 * caja (el número al lado ya dice 128%). No numérico → `null`.
 */
export function clampPct(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return Math.min(100, Math.max(0, number));
}

function startOfDay(date) {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
}
